import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Desktop, Mobile } from '../../../App';
import { useAppSelector } from '../../../app/hooks';

import { USDT, formatUSD } from '../../../helpers/helperFunctions';
import { CaptionText, H4, P1, P2, P3 } from '../../../styles/FontStyles';
import {
  Button,
  EarningBox,
  Flexed,
  PageTitle
} from '../../../styles/GlobalComponents';
import { color } from '../../../styles/GlobalStyles';
import {
  Icon,
  IconEarningsPartner,
  IconEarningsTotal,
  IconTodayEarnings
} from '../../../styles/Icons';
import {
  Flex,
  FlexBetween,
  FlexCenter,
  FloatingButtonBackoffice
} from '../../../styles/stylesStyled';

import { EarningsPageEnum, EarningsHistoryFilterEnum } from '../Earnings.types';

import { TextWithTooltip } from '../../../components/common/TextWithTooltip';
import { SidebarModal } from '../../../components/common/Sidebar/Sidebar';
import { WithdrawTwoFARequired } from '../Withdraw/WithdrawTwoFARequired';
import { Withdraw } from '../Withdraw/Withdraw';
import { NoKyc } from '../../Dashboard/NoKyc';
import axios from 'axios';

interface EarningsProps {
  setMobilePage: Dispatch<SetStateAction<EarningsPageEnum>>;
  withdraw: boolean;
  setWithdraw: Dispatch<SetStateAction<boolean>>;
  HandleChangeFilter: (filter: EarningsHistoryFilterEnum) => void;
}

export enum WithdrawSteps {
  FINAL = 1,
  TWO_FA,
  SUCCESS,
  KYC
}

export enum KysStatuses {
  NOT_STARTED = -1,
  INIT = 0,
  PENDING = 1,
  PRECHECKED = 2,
  QUEUED = 3,
  COMPLETED = 4,
  ON_HOLD = 5,
  AWAITING_SERVICE = 6,
  AWAITING_USER = 7
}

export const EarningsInfo = ({
  setMobilePage,
  HandleChangeFilter,
  withdraw,
  setWithdraw
}: EarningsProps) => {
  const { t } = useTranslation('earnings');

  const [step, setStep] = useState(WithdrawSteps.FINAL);
  const [amount, setAmount] = useState('');

  const [accessSDKToken, setAccessSDKToken] = useState<string>('');
  const [twoFaFactor, setTwoFaFactor] = useState(false);

  const [noKyc, setNoKyc] = useState(false);

  const { info } = useAppSelector((state) => state.earnings);
  const { verificationStatus } = useAppSelector((state) => state.settings);

  const handleWithdrawButton = async () => {
    const kycStatus = await getKYCStatuses();

    if (kycStatus === KysStatuses.COMPLETED) {
      setStep(WithdrawSteps.FINAL);

      setAmount('');

      if (!verificationStatus) {
        setTwoFaFactor(true);
      } else {
        setWithdraw(true);
      }
    } else {
      setNoKyc(true);
    }
  };

  const getAccessToken = async () => {
    try {
      const apiURL = process.env.REACT_APP_API_URL;
      const { data } = await axios.get(`${apiURL}/kyc/sdk-token`);

      setAccessSDKToken(data.data.token);
    } catch (error) {
      console.log(error);
    }
  };

  const getKYCStatuses = async () => {
    try {
      const apiURL = process.env.REACT_APP_API_URL;
      const { data } = await axios.get(`${apiURL}/kyc/status`);

      return data.data.status;
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getAccessToken();
  }, []);

  return (
    <div>
      <FlexBetween>
        <PageTitle>{t('title')}</PageTitle>

        <Mobile>
          <FloatingButtonBackoffice>
            <Flexed>
              <Button negative m='6px' onClick={handleWithdrawButton}>
                {t('withdraw')}
              </Button>
            </Flexed>
          </FloatingButtonBackoffice>
        </Mobile>

        <Mobile>
          <P1
            pointer
            color={color.colors.primary900}
            onClick={() => setMobilePage(EarningsPageEnum.EARNINGS_HISTORY)}>
            {t('show_history')}
          </P1>
        </Mobile>
      </FlexBetween>
      <Desktop style={{ marginBottom: 20 }}>
        <Button w='180px' m='6px' negative onClick={handleWithdrawButton}>
          {t('withdraw')}
        </Button>
      </Desktop>
      <FlexCenter style={{ marginBottom: 12 }}>
        <Icon color={color.colors.neutral900} mr='8'>
          <IconEarningsTotal />
        </Icon>
        <P3 color={color.colors.neutral900} bold>
          {t('total_earnings')}
        </P3>
      </FlexCenter>

      <Flexed rowSM gap='15px'>
        <EarningBox>
          <CaptionText mb='8' caps color={color.colors.neutral500}>
            {t('total_earnings')}
          </CaptionText>
          <Flexed center end='true'>
            <P2 bold mb='8' color={color.colors.neutral900}>
              {formatUSD(info?.totalEarnings?.totalEarnings)}
            </P2>
          </Flexed>
          <Flexed center end='true'>
            <FlexCenter>
              <Icon color={color.colors.secondary100}>
                <IconTodayEarnings />
              </Icon>
              <CaptionText ml='4' caps color={color.colors.secondary100} bold>
                {info?.totalEarnings?.todayTotalEarnings}
              </CaptionText>

              <CaptionText ml='4' caps color={color.colors.secondary100} bold>
                {t('today')}
              </CaptionText>
            </FlexCenter>
          </Flexed>
        </EarningBox>

        <EarningBox>
          <CaptionText mb='8' caps color={color.colors.neutral500}>
            {USDT} {t('balance')}
          </CaptionText>
          <Flexed center end='true'>
            <P2 bold mb='8' color={color.colors.neutral900}>
              {formatUSD(info?.totalEarnings?.availableBalance)}
            </P2>
          </Flexed>
          <Flexed center end='true'>
            <FlexCenter>
              <Icon color={color.colors.secondary100}>
                <IconTodayEarnings />
              </Icon>
              <CaptionText ml='4' caps color={color.colors.secondary100} bold>
                {info?.totalEarnings?.todayAvailableBalance}
              </CaptionText>

              <CaptionText ml='4' caps color={color.colors.secondary100} bold>
                {t('today')}
              </CaptionText>
            </FlexCenter>
          </Flexed>
        </EarningBox>
      </Flexed>

      <Flexed rowSM gap='15px'>
        <EarningBox>
          <TextWithTooltip
            margin='0'
            tooltip='MyLife points will be used to purchase discounts inside of Travelution Application and upcoming projects in Travelution Global ecosystem.'>
            <CaptionText caps color={color.colors.neutral500}>
              My Life Points
            </CaptionText>
          </TextWithTooltip>
          <div>
            <Flexed center end='true'>
              <P2 bold mb='8' color={color.colors.neutral900}>
                {formatUSD(info?.totalEarnings?.myLifePoints)}
              </P2>
            </Flexed>
            <Flexed center end='true'>
              <FlexCenter>
                <Icon color={color.colors.secondary100}>
                  <IconTodayEarnings />
                </Icon>
                <CaptionText ml='4' caps color={color.colors.secondary100} bold>
                  {info?.totalEarnings?.todayMyLifePoints}
                </CaptionText>

                <CaptionText ml='4' caps color={color.colors.secondary100} bold>
                  {t('today')}
                </CaptionText>
              </FlexCenter>
            </Flexed>
          </div>
        </EarningBox>
      </Flexed>

      <FlexCenter style={{ marginBottom: 12 }}>
        <Icon color={color.colors.neutral900} mr='8'>
          <IconEarningsPartner />
        </Icon>
        <P3 color={color.colors.neutral900} bold>
          {t('partner_earnings')}
        </P3>
      </FlexCenter>

      <EarningBox>
        <CaptionText mb='8' caps color={color.colors.neutral500}>
          {t('direct_bonus')}
        </CaptionText>

        <FlexBetween style={{ alignItems: 'end' }}>
          <Button
            fs='11px'
            h='30px'
            maxWidth='70px'
            negative
            style={{ fontWeight: 500 }}
            onClick={() =>
              HandleChangeFilter(EarningsHistoryFilterEnum.DIRECT_BONUS)
            }>
            INFO
          </Button>
          <div>
            <Flexed center end='true'>
              <P2 bold mb='8' color={color.colors.neutral900}>
                {formatUSD(info?.partnerEarnings?.directBonusEarnings)} {USDT}
              </P2>
            </Flexed>
            <Flexed center end='true'>
              <FlexCenter>
                <Icon color={color.colors.secondary100}>
                  <IconTodayEarnings />
                </Icon>
                <CaptionText ml='4' caps color={color.colors.secondary100} bold>
                  {info?.partnerEarnings?.todayDirectBonusEarnings}
                </CaptionText>

                <CaptionText ml='4' caps color={color.colors.secondary100} bold>
                  {t('today')}
                </CaptionText>
              </FlexCenter>
            </Flexed>
          </div>
        </FlexBetween>
      </EarningBox>

      <EarningBox>
        <CaptionText mb='8' caps color={color.colors.neutral500}>
          {t('team_bonus')}
        </CaptionText>

        <FlexBetween style={{ alignItems: 'end' }}>
          <Button
            fs='11px'
            h='30px'
            maxWidth='70px'
            negative
            style={{ fontWeight: 500 }}
            onClick={() =>
              HandleChangeFilter(EarningsHistoryFilterEnum.TEAM_BONUS)
            }>
            INFO
          </Button>
          <div>
            <Flexed center end='true'>
              <P2 bold mb='8' color={color.colors.neutral900}>
                {formatUSD(info?.partnerEarnings?.teamBonusEarnings)} {USDT}
              </P2>
            </Flexed>
            <Flexed center end='true'>
              <FlexCenter>
                <Icon color={color.colors.secondary100}>
                  <IconTodayEarnings />
                </Icon>
                <CaptionText ml='4' caps color={color.colors.secondary100} bold>
                  {info?.partnerEarnings?.todayTeamBonusEarnings}
                </CaptionText>

                <CaptionText ml='4' caps color={color.colors.secondary100} bold>
                  {t('today')}
                </CaptionText>
              </FlexCenter>
            </Flexed>
          </div>
        </FlexBetween>
      </EarningBox>

      <EarningBox>
        <CaptionText mb='8' caps color={color.colors.neutral500}>
          {t('matching_bonus')}
        </CaptionText>

        <FlexBetween style={{ alignItems: 'end' }}>
          <Button
            fs='11px'
            h='30px'
            maxWidth='70px'
            negative
            style={{ fontWeight: 500 }}
            onClick={() =>
              HandleChangeFilter(EarningsHistoryFilterEnum.MATCHING_BONUS)
            }>
            INFO
          </Button>
          <div>
            <Flexed center end='true'>
              <P2 bold mb='8' color={color.colors.neutral900}>
                {formatUSD(info?.partnerEarnings?.matchingBonusEarnings)} {USDT}
              </P2>
            </Flexed>
            <Flexed center end='true'>
              <FlexCenter>
                <Icon color={color.colors.secondary100}>
                  <IconTodayEarnings />
                </Icon>
                <CaptionText ml='4' caps color={color.colors.secondary100} bold>
                  {info?.partnerEarnings?.todayMatchingBonusEarnings}
                </CaptionText>

                <CaptionText ml='4' caps color={color.colors.secondary100} bold>
                  {t('today')}
                </CaptionText>
              </FlexCenter>
            </Flexed>
          </div>
        </FlexBetween>
      </EarningBox>

      <EarningBox>
        <CaptionText mb='8' caps color={color.colors.neutral500}>
          {t('cashback_bonus')}
        </CaptionText>

        <FlexBetween style={{ alignItems: 'end' }}>
          <Button
            fs='11px'
            h='30px'
            maxWidth='70px'
            negative
            style={{ fontWeight: 500 }}
            onClick={() =>
              HandleChangeFilter(EarningsHistoryFilterEnum.CASHBACK_BONUS)
            }>
            INFO
          </Button>
          <div>
            <Flexed center end='true'>
              <P2 bold mb='8' color={color.colors.neutral900}>
                {formatUSD(info?.partnerEarnings?.cashbackBonusEarnings)} {USDT}
              </P2>
            </Flexed>
            <Flexed center end='true'>
              <FlexCenter>
                <Icon color={color.colors.secondary100}>
                  <IconTodayEarnings />
                </Icon>
                <CaptionText ml='4' caps color={color.colors.secondary100} bold>
                  {info?.partnerEarnings?.todayCashbackBonusEarnings}
                </CaptionText>

                <CaptionText ml='4' caps color={color.colors.secondary100} bold>
                  {t('today')}
                </CaptionText>
              </FlexCenter>
            </Flexed>
          </div>
        </FlexBetween>
      </EarningBox>

      <EarningBox>
        <CaptionText mb='8' caps color={color.colors.neutral500}>
          {t('rank_rewards')}
        </CaptionText>

        <FlexBetween style={{ alignItems: 'end' }}>
          <Button
            fs='11px'
            h='30px'
            maxWidth='70px'
            negative
            style={{ fontWeight: 500 }}
            onClick={() =>
              HandleChangeFilter(EarningsHistoryFilterEnum.PACKAGE_RANK)
            }>
            INFO
          </Button>
          <div>
            <Flexed center end='true'>
              <P2 bold mb='8' color={color.colors.neutral900}>
                {formatUSD(info?.partnerEarnings?.packageRankRewardsEarnings)}{' '}
                {USDT}
              </P2>
            </Flexed>
            <Flexed center end='true'>
              <FlexCenter>
                <Icon color={color.colors.secondary100}>
                  <IconTodayEarnings />
                </Icon>
                <CaptionText ml='4' caps color={color.colors.secondary100} bold>
                  {info?.partnerEarnings?.todayPackageRankRewardsEarnings}
                </CaptionText>

                <CaptionText ml='4' caps color={color.colors.secondary100} bold>
                  {t('today')}
                </CaptionText>
              </FlexCenter>
            </Flexed>
          </div>
        </FlexBetween>
      </EarningBox>

      {info?.partnerEarnings?.legacyBonusEarnings !== 0 && (
        <EarningBox>
          <CaptionText mb='8' caps color={color.colors.neutral500}>
            {t('other_bonuses')}
          </CaptionText>

          <FlexBetween style={{ alignItems: 'end' }}>
            <Button
              fs='11px'
              h='30px'
              maxWidth='70px'
              negative
              style={{ fontWeight: 500 }}
              onClick={() =>
                HandleChangeFilter(EarningsHistoryFilterEnum.OTHER_BONUSES)
              }>
              INFO
            </Button>
            <div>
              <Flexed center end='true'>
                <P2 bold mb='8' color={color.colors.neutral900}>
                  {formatUSD(info?.partnerEarnings?.legacyBonusEarnings)} {USDT}
                </P2>
              </Flexed>
            </div>
          </FlexBetween>
        </EarningBox>
      )}

      <SidebarModal
        isOpen={withdraw}
        setOpen={setWithdraw}
        title={`${t('withdrawal')}`}
        sidebarWidth={'508px'}>
        <Withdraw
          step={step}
          setStep={setStep}
          amount={amount}
          setAmount={setAmount}
          setWithdraw={setWithdraw}
        />
      </SidebarModal>

      <SidebarModal
        isOpen={twoFaFactor}
        setOpen={setTwoFaFactor}
        title={t('verify_your_account')}
        sidebarWidth={'508px'}>
        <WithdrawTwoFARequired />
      </SidebarModal>

      <SidebarModal
        isOpen={noKyc}
        setOpen={setNoKyc}
        title={t('verify_your_account')}
        sidebarWidth={'508px'}>
        <NoKyc accessSDKToken={accessSDKToken} />
      </SidebarModal>
    </div>
  );
};
